import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Heart, X, ChevronDown, ChevronUp, PartyPopper } from 'lucide-react';
import { usePoller } from '../../hooks/usePoller';
import './celebrations.css';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 60000;

// BDAY-WISH-001 — dedicated, non-blocking birthday-wishes panel for the
// birthday employee. Separate from the notification bell / SSE stream.
export default function BirthdayWishesPanel() {
  const [wishes, setWishes] = useState([]);
  const [firstName, setFirstName] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const confettiFired = useRef(false);
  const lastRouteFetch = useRef(0);
  const location = useLocation();

  const fetchInbox = useCallback(async (signal) => {
    const token = localStorage.getItem('token');
    if (!token) { setWishes([]); return true; }
    try {
      const r = await fetch(`${API}/api/celebrations/wishes/inbox`, {
        headers: { Authorization: `Bearer ${token}` }, signal,
      });
      if (!r.ok) return false;
      const d = await r.json();
      setWishes(d.wishes || []);
      setFirstName(d.first_name || '');
      return true;
    } catch (e) { return false; }
  }, []);

  usePoller(fetchInbox, POLL_MS, { immediate: true });

  useEffect(() => {
    if (!localStorage.getItem('token')) return;
    const now = Date.now();
    if (now - lastRouteFetch.current < 20000) return;
    lastRouteFetch.current = now;
    fetchInbox();
  }, [location.pathname, fetchInbox]);

  useEffect(() => {
    if (!wishes.length || confettiFired.current) return;
    confettiFired.current = true;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    import('canvas-confetti').then(m => m.default({
      particleCount: 90, spread: 70, origin: { x: 0.9, y: 0.85 },
      colors: ['#f472b6', '#facc15', '#38bdf8', '#a78bfa'], disableForReducedMotion: true,
    })).catch(() => {});
  }, [wishes.length]);

  const ack = useCallback(async (ids, reaction) => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    // WISH-THANKS-001: "Thank everyone" is one idempotent server call (each ORIGINAL sender notified once);
    // anything that fails stays in the panel so it can be retried.
    if (reaction === 'thank_you' && ids.length > 1) {
      setWishes(prev => prev.filter(w => !ids.includes(w.id)));
      try {
        const r = await fetch(`${API}/api/celebrations/wishes/thank-all`, { method: 'POST', headers });
        const d = r.ok ? await r.json() : { failed: ids };
        if (d.failed?.length) fetchInbox();
      } catch { fetchInbox(); }
      return;
    }
    setWishes(prev => prev.filter(w => !ids.includes(w.id)));
    await Promise.all(ids.map(id => fetch(`${API}/api/celebrations/wishes/${id}/ack`, {
      method: 'POST', headers, body: JSON.stringify({ reaction }),
    }).then(r => { if (!r.ok) fetchInbox(); }).catch(() => fetchInbox())));
  }, [fetchInbox]);

  if (!wishes.length || location.pathname === '/login') return null;

  return createPortal(
    <aside className="wish-panel" data-testid="birthday-wishes-panel" aria-label="Wishes for you">
      <header className="wish-panel-head">
        <span className="wish-panel-icon"><PartyPopper size={15} /></span>
        <div className="wish-panel-title">
          <strong data-testid="birthday-wishes-title">Wishes for you{firstName ? `, ${firstName}` : ''}</strong>
          <small data-testid="birthday-wishes-count">{wishes.length} {wishes.length === 1 ? 'wish' : 'wishes'} waiting</small>
        </div>
        <button className="wish-panel-btn" onClick={() => setCollapsed(c => !c)}
                aria-label={collapsed ? 'Expand wishes' : 'Collapse wishes'} data-testid="birthday-wishes-toggle">
          {collapsed ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </header>
      {!collapsed && (
        <>
          <div className="wish-panel-list">
            {wishes.map((w, i) => (
              <article key={w.id} className="wish-card" style={{ animationDelay: `${Math.min(i, 6) * 70}ms` }}
                       data-testid={`birthday-wish-${w.id}`}>
                <div className="wish-card-from" data-testid="birthday-wish-sender" data-event-type={w.event_type || 'birthday'}>
                  {w.event_type === 'anniversary' ? `🎊 Work anniversary wish from ${w.sender_name}` : `🎂 Birthday wish from ${w.sender_name}`}
                </div>
                <p className="wish-card-msg" data-testid="birthday-wish-message">“{w.message}”</p>
                <div className="wish-card-actions">
                  <button className="wish-thanks" onClick={() => ack([w.id], 'thank_you')} data-testid={`birthday-wish-thanks-${w.id}`}>
                    <Heart size={13} /> Thank you
                  </button>
                  <button className="wish-dismiss" onClick={() => ack([w.id], 'dismissed')} data-testid={`birthday-wish-dismiss-${w.id}`}>
                    <X size={13} /> Dismiss
                  </button>
                </div>
              </article>
            ))}
          </div>
          {wishes.length > 1 && (
            <button className="wish-thank-all" onClick={() => ack(wishes.map(w => w.id), 'thank_you')} data-testid="birthday-wishes-thank-all">
              <Heart size={14} /> Thank everyone
            </button>
          )}
        </>
      )}
    </aside>,
    document.body
  );
}
